import React, { useState } from 'react';
import type { EntityMatch } from '../../types/frontend';

interface SymbolicMeaningsSectionProps {
  entities: EntityMatch[];
  selectedIds?: Set<string>;
  onToggleSelection?: (id: string) => void;
}

export const SymbolicMeaningsSection: React.FC<SymbolicMeaningsSectionProps> = ({ 
  entities,
  selectedIds = new Set(),
  onToggleSelection,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(true); // Default collapsed

  // Filter out results below 35% threshold (less than 35% of best match)
  const filteredEntities = entities.filter(entity => entity.confidence >= 0.35);

  if (filteredEntities.length === 0) {
    return (
      <div className="result-section">
        <div className="section-header">Meaningful symbols</div>
        <div className="section-empty">No specific symbols identified.</div>
      </div>
    );
  }

  return (
    <div className="result-section" data-testid="symbolic-meanings">
      <div
        className="section-header"
        style={{ cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <span>Meaningful symbols</span>
        <span className="chevron">{isCollapsed ? '▶' : '▼'}</span>
      </div>
      {!isCollapsed && (
        <div className="section-content">
          {filteredEntities.map(entity => {
            const getRelevanceLabel = (confidence: number) => {
              const pct = Math.round(confidence * 100);
              return pct === 100 ? 'Best match' : `${pct}% relevance`;
            };

            const isSelected = selectedIds.has(entity.id);
            
            return (
              <div 
                key={entity.id} 
                className="entity-card" 
                style={{ 
                  position: 'relative',
                  transform: isSelected ? 'translateX(12px)' : 'translateX(0)',
                  transition: 'transform 0.2s ease',
                  cursor: onToggleSelection ? 'pointer' : 'default',
                }}
                onClick={() => onToggleSelection?.(entity.id)}
                title={onToggleSelection ? (isSelected ? 'Remove from interpretation' : 'Add to interpretation') : undefined}
              >
                {/* Selection tab on left edge */}
                {onToggleSelection && (
                  <div
                    style={{
                      position: 'absolute',
                      left: '-8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: '8px',
                      height: '60%',
                      background: isSelected ? 'var(--lapis-lazuli)' : 'var(--stone-dark)',
                      borderRadius: '4px 0 0 4px',
                      transition: 'background 0.2s ease',
                      opacity: isSelected ? 1 : 0.3,
                    }}
                  />
                )}
                <div
                  className="entity-card-header"
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: 'var(--space-2)',
                    gap: 'var(--space-3)',
                    flexWrap: 'wrap',
                  }}
                >
                  <div className="entity-name">{entity.name}</div>
                  <span className="confidence-badge">{getRelevanceLabel(entity.confidence)}</span>
                </div>

              <div className="entity-summary">{entity.summary}</div>

              <div className="entity-category">
                {(() => {
                  // Known compound categories that should use "and"
                  const compoundCategories = ['events_rituals', 'rituals_events'];
                  const isCompound = compoundCategories.includes(entity.category);
                  
                  const text = isCompound
                    ? entity.category.replace(/_/g, ' and ')
                    : entity.category.replace(/_/g, ' ');
                  
                  return text
                    .split(' ')
                    .map((word, i) => {
                      // Keep small words lowercase unless first word
                      const lowerWords = ['and', 'or', 'of', 'the', 'in', 'a', 'an'];
                      if (i > 0 && lowerWords.includes(word.toLowerCase())) {
                        return word.toLowerCase();
                      }
                      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
                    })
                    .join(' ');
                })()}
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
